import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { HydrationHistoryItem } from '../../hydration-const/hydration-details-models';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { DashboardService } from '../../../dashboard/services/dashboard.service';

import { CalendarModule } from 'primeng/calendar';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';

@Component({
  selector: 'patient-hydration-history-list',
  standalone: true,
  imports: [CommonModule, DialogModule, CalendarModule, FormsModule, Button, DropdownModule],
  templateUrl: './hydration-history-list.component.html',
  styleUrl: './hydration-history-list.component.css',
})
export class HydrationHistoryListComponent {
  @Input() history: HydrationHistoryItem[] = [];
  @Input() trackId!: string | null;
  @Output() hydrationUpdated = new EventEmitter<void>();

  private authService = inject(AuthService);
  private dashboardService = inject(DashboardService);

  displayModal: boolean = false;
  selectedRow: HydrationHistoryItem | null = null;
  updating: boolean = false;

  // Track local edits independently of backend
  stagedValue: number = 0;

  intakeOptions = [
    { label: '200 ml', value: 200 },
    { label: '250 ml', value: 250 },
    { label: '300 ml', value: 300 },
    { label: '400 ml', value: 400 },
    { label: '500 ml', value: 500 },
  ];
  selectedIntake: number = 200;

  // Date Filter State
  selectedDate: Date | null = null;
  filteredHistory: HydrationHistoryItem[] = [];

  ngOnChanges() {
    this.applyDateFilter();
  }

  applyDateFilter() {
    if (!this.selectedDate) {
      this.filteredHistory = [...this.history];
      return;
    }

    const start = new Date(this.selectedDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(this.selectedDate);
    end.setHours(23, 59, 59, 999);

    this.filteredHistory = this.history.filter((item) => {
      const parts = item.date.split('-');
      if (parts.length !== 3) return true;
      const itemDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      return itemDate.getTime() >= start.getTime() && itemDate.getTime() <= end.getTime();
    });
  }

  openDetails(item: HydrationHistoryItem) {
    if (!this.canEdit(item.date)) return;
    this.selectedRow = { ...item }; // clone it for modal
    this.stagedValue = item.value;
    this.displayModal = true;
  }

  canEdit(dateStr: string): boolean {
    // True if date is Today or Yesterday
    if (!dateStr) return false;

    // API returns DD-MM-YYYY format in JSON e.g. "27-03-2026"
    const parts = dateStr.split('-');
    if (parts.length !== 3) return false;

    // Build Date object strictly avoiding timezone shifting bugs by comparing midnight
    const itemDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    itemDate.setHours(0, 0, 0, 0);

    return itemDate.getTime() === today.getTime() || itemDate.getTime() === yesterday.getTime();
  }

  getDisplayLabel(dateStr: string): string {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const itemDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    itemDate.setHours(0, 0, 0, 0);

    if (itemDate.getTime() === today.getTime()) return 'Today';
    if (itemDate.getTime() === yesterday.getTime()) return 'Yesterday';

    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[itemDate.getDay()];
  }

  formatLiters(ml: number): number {
    return Number((ml / 1000).toFixed(1));
  }

  getProgressComplete(intake: number, goal: number): number {
    if (!goal) return 0;
    return Math.min(Math.round((intake / goal) * 100), 100);
  }

  // --- Modal Specific Logic (Duplicated from patient-hydration-card) ---

  get percentage(): number {
    if (!this.selectedRow) return 0;
    return this.getProgressComplete(this.stagedValue, this.selectedRow.goal);
  }

  getGlassFill(index: number): number {
    if (!this.selectedRow) return 0;
    const totalGlasses = 5;
    const ratio = this.stagedValue / this.selectedRow.goal;
    const fill = ratio * totalGlasses - index;
    return Math.max(0, Math.min(1, fill));
  }

  increment() {
    if (!this.selectedRow || this.updating) return;
    this.stagedValue += this.selectedIntake;
  }

  decrement() {
    if (!this.selectedRow || this.updating) return;
    if (this.stagedValue >= this.selectedIntake) {
      this.stagedValue -= this.selectedIntake;
    } else if (this.stagedValue > 0) {
      this.stagedValue = 0;
    }
  }

  saveUpdates() {
    if (!this.selectedRow || this.stagedValue === this.selectedRow.value) {
      this.displayModal = false;
      return;
    }

    // Only hit API when saving
    const difference = this.stagedValue - this.selectedRow.value;
    this.logHydration(difference);
  }

  private logHydration(amount: number) {
    if (!this.selectedRow) return;
    this.updating = true;
    this.authService.getUserProfile().subscribe((user) => {
      if (user?.PatientId) {
        // Construct ISO date matching the selected row's historical date.
        // E.g. "27-03-2026"
        const parts = this.selectedRow!.date.split('-');
        const itemDateStr = `${parts[2]}-${parts[1]}-${parts[0]}`;

        const payload = {
          patient_id: user.PatientId,
          tracking_id: this.trackId,
          amount_ml: amount,
          log_source: 1,
          log_date: itemDateStr,
        };
        this.dashboardService.logHydration(payload).subscribe({
          next: () => {
            this.updating = false;
            this.displayModal = false;
            this.hydrationUpdated.emit();
          },
          error: (err) => {
            console.error('Error logging hydration', err);
            this.updating = false;
            // Dont close modal on error, let them try again
          },
        });
      }
    });
  }
}
